/**
 * Explore-tab themes — the same signals re-grouped into 7 areas a marketer
 * manages, rather than the data-model groupings used elsewhere in the app.
 *
 * Per the SYR-68 decision: presentation-only curation. The signal lists below
 * use canonical names; the view applies `getDisplayName` from `utils/elementDisplayNames`
 * so e.g. Alignment Depth renders as "Touchpoints" and Standardised NPS as
 * "Advocacy Intent".
 *
 * `tone` is a hex from the existing brand-equity-app palette — it sets the
 * theme icon background and divider accents.
 */

import {
  Package,
  Tag,
  Star,
  Eye,
  Heart,
  Handshake,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface ThemeConfig {
  id: string;
  /** Marketer-facing theme title. */
  title: string;
  /** One-line description of what this theme covers (Explore-tab chrome). */
  covers: string;
  /** Theme icon — matches the design hand-off (lucide-react). */
  icon: LucideIcon;
  /** Hex tone — picked from the BAND_PALETTE family or a neutral. */
  tone: string;
  /** Canonical names of signals in this theme. */
  signals: readonly string[];
}

export const THEMES: readonly ThemeConfig[] = Object.freeze([
  {
    id: 'offering',
    title: 'Your Offering',
    covers: 'What you actually sell: the product itself.',
    icon: Package,
    tone: '#16a34a',
    signals: ['Product Performance', 'Product Richness'],
  },
  {
    id: 'pricing',
    title: 'Pricing & Value',
    covers:
      'Whether you’re worth the money, and what discounting is doing to you.',
    icon: Tag,
    tone: '#d97706',
    signals: [
      'Quality Value Tradeoff (PQV)',
      'Perceived Value for the Cost',
      'Price',
      'Price Deals',
    ],
  },
  {
    id: 'standout',
    title: 'Standing Out',
    covers: 'How distinctive and memorable you are versus everyone else.',
    icon: Star,
    tone: '#dc2626',
    signals: [
      'Uniqueness',
      'Associations',
      'Advertising Content',
      'Symbolic Recall',
    ],
  },
  {
    id: 'known',
    title: 'Being Known',
    covers:
      'Whether the market beyond your customers knows you exist.',
    icon: Eye,
    tone: '#dc2626',
    signals: [
      'Brand Familiarity',
      'Market Awareness',
      'Alignment Depth', // → "Touchpoints"
      'Distribution Intensity', // → "Availability"
      'Brand Environment',
      'Prominence',
      'Advertising Intensity',
      'Unprompted Brand Awareness',
      'Prompted Brand Awareness',
    ],
  },
  {
    id: 'trust',
    title: 'Trust & Feeling',
    covers: 'Do people trust you and feel good about you.',
    icon: Handshake,
    tone: '#d97706',
    signals: ['Brand Valence', 'Brand Empathy', 'Staff Behaviour', 'Brand Trust'],
  },
  {
    id: 'belonging',
    title: 'Belonging & Identity',
    covers: 'How much the brand is part of who your customers are.',
    icon: Users,
    tone: '#16a34a',
    signals: [
      'Self Congruence',
      'User Congruence',
      'Brand Identification',
      'Brand Self Connection',
    ],
  },
  {
    id: 'loyalty',
    title: 'Loyalty & Advocacy',
    covers: 'Do they come back, and do they bring others.',
    icon: Heart,
    tone: '#d97706',
    signals: [
      'Standardised NPS', // → "Advocacy Intent"
      'Repurchase Intention',
      'Self Reported Loyalty',
      'Parity Preference',
    ],
  },
]);

/** Lookup by stable id. */
export function getThemeConfig(id: string): ThemeConfig | undefined {
  return THEMES.find((t) => t.id === id);
}
